import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import StepShell from "../components/StepShell";
import AyupathCard from "../components/AyupathCard";
import Field from "../components/Field";
import Input from "../components/Input";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";

import { useForm } from "../context/FormContext";
import { buildDobISO, buildTime12, buildTime24, isValidEmail } from "../utils";
import { describeDob } from "./StepDob";
import { submitTeaserRequest } from "../services/teaserApi";
import { track } from "../utils/analytics";
import useEnterToContinue from "../hooks/useEnterToContinue";
import TurnstileWidget from "../components/TurnstileWidget";

const TOTAL = 6;
// Bot check is shown only when a site key is configured for this build.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function StepEmail() {
  const { formData, setFormData } = useForm();
  const navigate = useNavigate();
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const emailError = useMemo(() => {
    if (!touched) return "";
    if (!formData.email.trim()) return "Please enter your email address.";
    if (!isValidEmail(formData.email)) return "Please enter a valid email address.";
    return "";
  }, [formData.email, touched]);

  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const turnstileReady = !TURNSTILE_SITE_KEY || Boolean(turnstileToken);

  // Every date in the reading is built on these, and a neighbouring year in
  // the list is one tap away, so the reader confirms them once, together.
  const [detailsConfirmed, setDetailsConfirmed] = useState(false);
  const born = describeDob(formData.dob);
  const time = buildTime12(formData.birthTime);
  const place = formData.locationData?.displayName || formData.location;

  const canSubmit = !emailError && isValidEmail(formData.email) && formData.serviceEmailConsent && detailsConfirmed
    && turnstileReady && !submitting;

  useEnterToContinue(() => submit(), canSubmit);

  async function submit() {
    setTouched(true);
    setSubmitError("");

    if (!canSubmit) return;

    try {
      setSubmitting(true);

      // Primary path: Submit to Azure Functions backend → navigate immediately on 202
      const teaserPayload = {
        name: formData.name.trim(),
        dob: buildDobISO(formData.dob),
        birthTime: buildTime24(formData.birthTime),
        birthTimeConfidence: formData.birthTimeConfidence,
        location: formData.locationData || {
          lat: null,
          lon: null,
          city: formData.location.trim(),
          country: null
        },
        email: formData.email.trim(),
        focusArea: formData.focusArea,
        landingVariant: formData.landingVariant,
        serviceEmailConsent: formData.serviceEmailConsent,
        marketingConsent: formData.marketingConsent,
        turnstileToken
      };
      const result = await submitTeaserRequest(teaserPayload);
      const requestId = result.requestId;

      // The past-window questions come first; the preview is written meanwhile.
      navigate(`/questions?requestId=${encodeURIComponent(requestId)}`, { replace: true, state: { requestId } });

    } catch (e) {
      console.error('Teaser submission failed:', e.message, e);
      // The token was spent on this attempt; a retry needs a fresh one.
      if (TURNSTILE_SITE_KEY) {
        setTurnstileToken(null);
        setTurnstileKey((k) => k + 1);
      }
      const isNetworkOrCors =
        !e.message ||
        e.message.toLowerCase().includes('failed to fetch') ||
        e.message.toLowerCase().includes('network') ||
        e.message.toLowerCase().includes('aborted');
      setSubmitError(
        isNetworkOrCors
          ? 'Could not reach the server. Please check your connection and try again.'
          : e.message || 'Could not send right now. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <StepShell step={6} totalSteps={TOTAL} title="Your next chapter">
      <AyupathCard>
        <div className="space-y-5">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Where should we send your preview?</h2>
            <p className="text-gray-600 mt-1">Read your personal preview here and receive a copy by email.</p>
          </div>

          <Field label="Email address" error={emailError}>
            <Input
              type="email"
              aria-label="Email address"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              onBlur={() => setTouched(true)}
              placeholder="you@example.com"
              inputMode="email"
              autoComplete="email"
              autoFocus
            />
          </Field>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-gray-900">Email preferences</legend>
            <label className="flex items-start gap-3 py-3 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={formData.serviceEmailConsent}
                onChange={(event) => setFormData({ ...formData, serviceEmailConsent: event.target.checked })}
                className="h-5 w-5 shrink-0 accent-orange-600" />
              I agree to receive my requested AyuPath preview by email. This does not subscribe me to marketing.
            </label>
            {/* Worded as the reminder it is, so readers see what they get by ticking it. */}
            <label className="flex items-start gap-3 rounded-2xl border-2 border-orange-200 bg-orange-50 p-3 text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" checked={formData.marketingConsent}
                onChange={(event) => {
                  setFormData({ ...formData, marketingConsent: event.target.checked });
                  if (event.target.checked) track("marketing_consent_opt_in");
                }}
                className="mt-0.5 h-5 w-5 shrink-0 accent-orange-600" />
              <span>
                <strong className="text-gray-900">Remind me before my next chapter begins.</strong>
                <span className="block text-gray-600">
                  Plus a few AyuPath offers. At most 2 emails a month, unsubscribe in one click.
                </span>
              </span>
            </label>
          </fieldset>
          {!formData.serviceEmailConsent ? (
            <p className="text-sm text-gray-600">Please agree to receive your requested preview before submitting.</p>
          ) : null}


          <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 space-y-3">
            <p className="font-semibold text-gray-900">Please check your birth details</p>
            <dl className="text-sm text-gray-800 space-y-1.5">
              <DetailRow label="Born" onEdit={() => navigate("/dob")}>
                {born ? <>{born.date}{born.age >= 0 ? ` (you're ${born.age})` : ""}</> : "—"}
              </DetailRow>
              <DetailRow label="Time" onEdit={() => navigate("/birth-time")}>{time || "—"}</DetailRow>
              <DetailRow label="Place" onEdit={() => navigate("/location")}>{place || "—"}</DetailRow>
            </dl>
            <label className="flex items-start gap-3 text-sm text-gray-800 cursor-pointer">
              <input type="checkbox" checked={detailsConfirmed}
                onChange={(event) => setDetailsConfirmed(event.target.checked)}
                className="h-5 w-5 shrink-0 accent-orange-600" />
              These are correct. Every date in my reading is built on them.
            </label>
          </div>

          {TURNSTILE_SITE_KEY ? (
            <TurnstileWidget key={turnstileKey} siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} />
          ) : null}

          {submitError ? (
            <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-700">
              {submitError}
            </div>
          ) : null}

          <PrimaryButton disabled={!canSubmit} onClick={submit}>
            {submitting ? "Submitting…" : "Submit"}
          </PrimaryButton>

          <div className="text-center">
            <SecondaryButton onClick={() => navigate("/focus")}>Edit previous answers</SecondaryButton>
          </div>
        </div>
      </AyupathCard>
    </StepShell>
  );
}

function DetailRow({ label, onEdit, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-12 shrink-0 text-gray-600">{label}</dt>
      <dd className="flex-1 font-medium text-gray-900">{children}</dd>
      <button type="button" onClick={onEdit} className="text-orange-700 underline underline-offset-2">
        Change<span className="sr-only"> {label.toLowerCase()}</span>
      </button>
    </div>
  );
}
